import { useEffect, useState } from "react";
import { RefreshCw, Search, ShieldCheck } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  loadAdminPqrPage,
  updateAdminPqrStatus,
  type AdminClientError,
} from "@/features/admin/api/adminClient";
import { AdminPqrTable } from "@/features/admin/components/AdminPqrTable";
import type { AdminPqrListFilters } from "@/features/admin/admin.types";
import {
  deletePqrSubmission,
  loadAdminPqrRequestDetails,
  replyToPqrSubmission,
} from "@/features/pqr/api/pqrAdminClient";
import { loadPqrReplyThread, toPqrClientError } from "@/features/pqr/api/pqrClient";
import { createPqrAttachmentSignedLinks } from "@/features/pqr/api/pqrStorage";
import { PQRRequestDetails } from "@/features/pqr/components/PQRRequestDetails";
import { getPqrCategoryTranslationKey } from "@/features/pqr/pqrMappers";
import { pqrCategories, type PqrStatus } from "@/features/pqr/pqr.types";
import { getPqrErrorTranslationKey, pqrReplySchema } from "@/features/pqr/validation/pqrSchemas";

const defaultFilters: AdminPqrListFilters = {
  category: "all",
  page: 1,
  searchText: "",
  status: "all",
};

async function loadSelectedRequest(requestId: string) {
  const [details, threadMessages] = await Promise.all([
    loadAdminPqrRequestDetails(requestId),
    loadPqrReplyThread(requestId),
  ]);

  const attachmentLinks = details.attachmentPaths.length
    ? await createPqrAttachmentSignedLinks(details.attachmentPaths)
    : [];

  return {
    attachmentLinks,
    details,
    threadMessages,
  };
}

export function AdminPqrPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketRefParam = searchParams.get("ticketRef") ?? "";
  const [filters, setFilters] = useState<AdminPqrListFilters>({
    ...defaultFilters,
    searchText: ticketRefParam,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageData, setPageData] = useState<Awaited<ReturnType<typeof loadAdminPqrPage>> | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [details, setDetails] = useState<Awaited<ReturnType<typeof loadAdminPqrRequestDetails>> | null>(null);
  const [attachmentLinks, setAttachmentLinks] = useState<Awaited<ReturnType<typeof createPqrAttachmentSignedLinks>>>([]);
  const [threadMessages, setThreadMessages] = useState<Awaited<ReturnType<typeof loadPqrReplyThread>>>([]);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replyBusy, setReplyBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setFilters((current) => (
      current.searchText === ticketRefParam
        ? current
        : {
            ...current,
            page: 1,
            searchText: ticketRefParam,
          }
    ));
  }, [ticketRefParam]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void loadAdminPqrPage(filters)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setPageData(result);
        setSelectedRequestId((current) => {
          const ticketMatch = ticketRefParam
            ? result.items.find((item) => item.ticketRef === ticketRefParam)
            : null;

          if (ticketMatch) {
            return ticketMatch.id;
          }

          return result.items.some((item) => item.id === current)
            ? current
            : result.items[0]?.id ?? null;
        });
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : t("pqr.errors.adminLoad"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, ticketRefParam, t]);

  useEffect(() => {
    if (!selectedRequestId) {
      setDetails(null);
      setAttachmentLinks([]);
      setThreadMessages([]);
      setReplyDraft("");
      setReplyError(null);
      setDetailError(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);

    void loadSelectedRequest(selectedRequestId)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setAttachmentLinks(result.attachmentLinks);
        setDetails(result.details);
        setThreadMessages(result.threadMessages);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setDetailError(loadError instanceof Error ? loadError.message : t("pqr.errors.detailLoad"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRequestId, t]);

  async function refreshPage(preferredRequestId = selectedRequestId) {
    const nextPage = await loadAdminPqrPage(filters);
    setPageData(nextPage);

    const nextSelectedId = preferredRequestId && nextPage.items.some((item) => item.id === preferredRequestId)
      ? preferredRequestId
      : nextPage.items[0]?.id ?? null;

    setSelectedRequestId(nextSelectedId);

    if (!nextSelectedId) {
      setDetails(null);
      setAttachmentLinks([]);
      setThreadMessages([]);
      return;
    }

    const nextRequest = await loadSelectedRequest(nextSelectedId);
    setAttachmentLinks(nextRequest.attachmentLinks);
    setDetails(nextRequest.details);
    setThreadMessages(nextRequest.threadMessages);
  }

  function handleOpenRequest(requestId: string) {
    if (searchParams.has("ticketRef")) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete("ticketRef");
      setSearchParams(nextSearchParams, { replace: true });
    }

    setSelectedRequestId(requestId);
  }

  async function handleStatusChange(status: PqrStatus) {
    if (!details) {
      return;
    }

    setActionBusy(true);
    setError(null);
    setNotice(null);

    try {
      await updateAdminPqrStatus({
        nextStatus: status,
        requestId: details.id,
      });
      await refreshPage(details.id);
      setNotice(t("pqr.admin.statusUpdated"));
    } catch (updateError) {
      const clientError = updateError as AdminClientError;
      setError(clientError.message || t("pqr.errors.statusUpdate"));
    } finally {
      setActionBusy(false);
    }
  }

  async function handleReplySubmit() {
    if (!details) {
      return;
    }

    const parsedReply = pqrReplySchema.safeParse({
      message: replyDraft,
    });

    if (!parsedReply.success) {
      setReplyError(t(parsedReply.error.issues[0]?.message ?? "pqr.errors.generic"));
      return;
    }

    setReplyBusy(true);
    setReplyError(null);
    setNotice(null);

    try {
      const reply = await replyToPqrSubmission(details.id, parsedReply.data.message);
      setThreadMessages((current) => [...current, reply]);
      setReplyDraft("");
      setNotice(t("pqr.admin.replySent"));
    } catch (replyErrorValue) {
      const clientError = toPqrClientError(replyErrorValue);
      setReplyError(t(getPqrErrorTranslationKey(clientError.code)));
    } finally {
      setReplyBusy(false);
    }
  }

  async function handleDelete() {
    if (!details) {
      return;
    }

    const shouldDelete = window.confirm(
      t("pqr.admin.deleteConfirm", { ticketRef: details.ticketRef }),
    );
    if (!shouldDelete) {
      return;
    }

    setActionBusy(true);
    setError(null);
    setNotice(null);

    try {
      await deletePqrSubmission(details.id);
      await refreshPage(null);
      setNotice(t("pqr.admin.deleteSuccess"));
    } catch (deleteErrorValue) {
      setError(deleteErrorValue instanceof Error ? deleteErrorValue.message : t("pqr.errors.deleteFailed"));
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-[1.7rem] border border-white/8 bg-white/[0.035] px-5 py-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.34em] text-[#b69d7e]">
              <ShieldCheck className="size-4" />
              {t("pqr.admin.eyebrow")}
            </div>
            <h2 className="mt-3 font-display text-4xl text-[#fff7ef]">{t("pqr.admin.title")}</h2>
            <p className="mt-3 text-sm leading-7 text-[#d7cab9]/76">
              {t("pqr.admin.subtitle")}
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,280px)_180px_180px_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#9d886d]" />
              <Input
                className="border-white/10 bg-black/20 pl-11 text-[#fff7ef] placeholder:text-[#8f7a63]"
                placeholder={t("pqr.admin.searchPlaceholder")}
                value={filters.searchText}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  page: 1,
                  searchText: event.target.value,
                }))}
              />
            </div>

            <select
              aria-label={t("adminWorkspace.common.filterStatus")}
              className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none transition focus:border-[#d2b793]/40 focus:ring-2 focus:ring-[#d2b793]/20"
              value={filters.status}
              onChange={(event) => setFilters((current) => ({
                ...current,
                page: 1,
                status: event.target.value as AdminPqrListFilters["status"],
              }))}
            >
              <option value="all">{t("pqr.admin.filterAllStatuses")}</option>
              <option value="open">{t("pqr.status.open")}</option>
              <option value="in_review">{t("pqr.status.in_review")}</option>
              <option value="resolved">{t("pqr.status.resolved")}</option>
              <option value="closed">{t("pqr.status.closed")}</option>
            </select>

            <select
              aria-label={t("adminWorkspace.common.filterCategory")}
              className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none transition focus:border-[#d2b793]/40 focus:ring-2 focus:ring-[#d2b793]/20"
              value={filters.category}
              onChange={(event) => setFilters((current) => ({
                ...current,
                category: event.target.value as AdminPqrListFilters["category"],
                page: 1,
              }))}
            >
              <option value="all">{t("pqr.admin.filterAllCategories")}</option>
              {pqrCategories.map((category) => (
                <option key={category} value={category}>
                  {t(getPqrCategoryTranslationKey(category))}
                </option>
              ))}
            </select>

            <Button
              type="button"
              variant="outline"
              onClick={() => void refreshPage()}
            >
              <RefreshCw className="size-4" />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      {notice ? (
        <div className="rounded-[1.4rem] border border-[#3e5f42]/30 bg-[#203422]/35 px-5 py-4 text-sm text-[#d8f0db]">
          {notice}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-[1.4rem] border border-[#7b3430]/35 bg-[#6d2d29]/25 px-5 py-4 text-sm text-[#f6d8d2]">
          {error}
        </div>
      ) : null}

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.02fr)]">
        <AdminPqrTable
          activeRequestId={selectedRequestId}
          isLoading={loading}
          items={pageData?.items ?? []}
          page={pageData?.page ?? filters.page}
          pageCount={pageData?.pageCount ?? 1}
          totalCount={pageData?.totalCount ?? 0}
          onOpenRequest={handleOpenRequest}
          onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        />

        <PQRRequestDetails
          adminReply={
            details
              ? {
                  busy: replyBusy,
                  canReply: Boolean(details.userId),
                  disabledReason: details.userId ? null : t("pqr.thread.replyDisabledGuest"),
                  draft: replyDraft,
                  error: replyError,
                  onDraftChange: setReplyDraft,
                  onSubmit: handleReplySubmit,
                }
              : undefined
          }
          attachmentLinks={attachmentLinks}
          details={details}
          emptyBody={t("pqr.admin.emptySelection")}
          errorMessage={detailError}
          isLoading={detailLoading}
          onDelete={handleDelete}
          onOpenUserProfile={details?.userId ? () => navigate(`/admin/users?userId=${details.userId}`) : undefined}
          onStatusChange={handleStatusChange}
          statusActionBusy={actionBusy}
          threadMessages={threadMessages}
          title={t("pqr.admin.detailTitle")}
        />
      </section>
    </div>
  );
}
